import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "透過PNG（透明背景）の作り方と使い方【初心者向け完全ガイド】",
	description:
		"透過PNG（透明背景）の作り方を初心者向けに解説。AIツールで簡単に作る方法から、EC出品・PowerPoint・Webデザインでの正しい使い方まで網羅。",
	publishDate: "2026-03-24",
	slug: "transparent-png-beginners-guide",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function TransparentPngBeginnersGuide() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<section>
						<p className="text-gray-700 mb-6">
							「透過PNG」「透明背景のPNG」という言葉を聞いたことはあるでしょうか。
							商品画像・ロゴ・デザイン素材など、様々な場面で使われる重要なファイル形式です。
							この記事では、透過PNGとは何か・どうやって作るか・どこで使えるかを初心者向けに解説します。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							透過PNG（透明背景）とは？
						</h2>
						<p className="text-gray-700 mb-4">
							PNGファイルには「アルファチャンネル」という透明情報を保存できる機能があります。
							この機能を使って背景部分を透明にした画像が「透過PNG」です。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ul className="space-y-2">
								<li>
									<span className="font-semibold">透明の見た目：</span>
									画像編集ソフトでは市松模様（チェッカー柄）で表示される
								</li>
								<li>
									<span className="font-semibold">Webブラウザでの表示：</span>
									ページの背景色がそのまま透けて見える
								</li>
								<li>
									<span className="font-semibold">JPEGとの違い：</span>
									JPEGは透明を保存できない。透明情報が失われ白や黒で塗りつぶされる
								</li>
							</ul>
						</div>
						<div className="bg-yellow-50 rounded-xl p-4 mb-6 text-sm text-gray-700">
							<p className="font-semibold mb-1">よくある誤解</p>
							<p>
								透過PNGをJPEGに変換すると透明部分が消えます。EC出品で白背景が必要な場合は、
								透過PNGをJPEGに変換するのではなく、白背景を塗った状態でJPEGとして保存するのが正しい手順です。
							</p>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">透過PNGの作り方</h2>

						<h3 className="text-lg font-semibold mt-6 mb-3">
							方法1：しろぬきや（AIで自動作成・無料・登録不要）
						</h3>
						<p className="text-gray-700 mb-4">
							最も簡単な方法は、AIが自動で背景を除去してくれるツールを使うことです。
							しろぬきやならブラウザだけで透過PNGを作成できます。
						</p>
						<div className="bg-indigo-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<p className="font-semibold mb-2">
								しろぬきやで透過PNGを作る手順
							</p>
							<ol className="list-decimal list-inside space-y-1">
								<li>しろぬきや（shironukiya.fucina.casa）を開く</li>
								<li>背景を消したい画像をアップロード</li>
								<li>AIが自動で背景を除去（数秒）</li>
								<li>「透過PNG」を選択してダウンロード</li>
							</ol>
						</div>
						<p className="text-gray-700 mb-4">
							商品写真・ロゴ・人物写真など、被写体を問わず使えます。
							無料で登録不要のため、初めて透過PNGを作る方にも最適です。
						</p>

						<CtaBlock />

						<h3 className="text-lg font-semibold mt-8 mb-3">
							方法2：Photoshop（プロ向け・高精度）
						</h3>
						<p className="text-gray-700 mb-4">
							Adobe
							Photoshopの「選択とマスク」機能を使い、被写体を精密に選択して背景を削除します。
							最高精度の透過処理が可能ですが、月額サブスクが必要で操作習熟にも時間がかかります。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ol className="list-decimal list-inside space-y-1">
								<li>Photoshopで画像を開く</li>
								<li>「選択範囲」→「被写体を選択」で自動選択</li>
								<li>「選択とマスク」で境界を調整</li>
								<li>レイヤーマスクを適用</li>
								<li>
									PNG形式で書き出し（「ファイル」→「書き出し」→「PNG形式で書き出し」）
								</li>
							</ol>
						</div>

						<h3 className="text-lg font-semibold mt-8 mb-3">
							方法3：GIMP（無料・インストール型）
						</h3>
						<p className="text-gray-700 mb-4">
							無料の画像編集ソフトGIMPでも透過PNGを作れます。
							単色背景であれば「ファジー選択」→削除→PNG書き出しの手順で対応できます。
						</p>
						<div className="bg-gray-50 rounded-xl p-5 mb-4 text-sm text-gray-700">
							<ol className="list-decimal list-inside space-y-1">
								<li>GIMPで画像を開く</li>
								<li>「画像」→「アルファチャンネルの追加」（必須）</li>
								<li>「ファジー選択」で背景をクリック</li>
								<li>DeleteキーまたはEdit→消去</li>
								<li>「ファイル」→「名前を付けてエクスポート」→PNG形式で保存</li>
							</ol>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							透過PNGの正しい使い方
						</h2>

						<h3 className="text-lg font-semibold mt-6 mb-3">
							EC出品での使い方
						</h3>
						<p className="text-gray-700 mb-4">
							AmazonやQoo10など一部のモールでは透過PNGをそのままアップロードすると、
							表示する環境によって背景が黒や灰色に見える問題が起きます。
						</p>
						<div className="bg-red-50 rounded-xl p-4 mb-4 text-sm text-gray-700">
							<p className="font-semibold mb-1">
								注意：透過PNGをJPEGに変換する際の落とし穴
							</p>
							<p>
								透過PNGをWindowsの「名前を付けて保存」でJPEGに変換すると、
								透明部分が黒や灰色で塗りつぶされることがあります。
								白背景JPEGが必要な場合は、しろぬきやの「白背景変換」機能を使うのが確実です。
							</p>
						</div>

						<h3 className="text-lg font-semibold mt-6 mb-3">
							PowerPoint・Keynoteでの使い方
						</h3>
						<p className="text-gray-700 mb-4">
							透過PNGをスライドに挿入すると、スライドの背景がそのまま透けて見えます。
							白いスライドに貼れば白背景に、カラー背景のスライドに貼ればその色が背景になります。
							PowerPointの「背景の削除」機能を使うよりも精度が高く仕上がるため、
							しろぬきやで透過PNGを作ってから挿入するのがおすすめです。
						</p>

						<h3 className="text-lg font-semibold mt-6 mb-3">
							Webデザインでの使い方
						</h3>
						<p className="text-gray-700 mb-4">
							WebサイトやバナーにPNGを使う場合、透過PNGであればどんな背景色のページにも馴染みます。
							ロゴや商品のカットアウト画像を作る際に特に有効です。
						</p>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							透過PNGに関するよくある質問
						</h2>
						<div className="space-y-6">
							<div className="border rounded-xl p-5">
								<p className="font-semibold text-gray-800 mb-2">
									Q. 透過PNGを作ったのに背景が灰色になる
								</p>
								<p className="text-sm text-gray-700">
									A.
									画像ビューアやJPEG変換の際に透明部分がグレーで塗りつぶされている可能性があります。
									白背景が必要な場合は、透過PNGに白を合成してからJPEGとして保存してください。
									しろぬきやの白背景変換機能を使えばワンクリックで対応できます。
								</p>
							</div>
							<div className="border rounded-xl p-5">
								<p className="font-semibold text-gray-800 mb-2">
									Q. AmazonにPNGをアップロードしたら背景が白にならない
								</p>
								<p className="text-sm text-gray-700">
									A.
									Amazonは透過PNGをそのまま使うと表示が不安定になることがあります。
									背景を白で塗りつぶしたJPEGまたはPNGに変換してからアップロードするのが確実です。
								</p>
							</div>
							<div className="border rounded-xl p-5">
								<p className="font-semibold text-gray-800 mb-2">
									Q. 透過PNGはファイルサイズが大きくなりますか？
								</p>
								<p className="text-sm text-gray-700">
									A. JPEGより大きくなる傾向があります。
									ファイルサイズが問題になる場合はWebP形式（PNG並みの透明サポート＋高圧縮）への変換も検討してください。
								</p>
							</div>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">まとめ</h2>
						<p className="text-gray-700 mb-4">
							透過PNGは商品画像・プレゼン・Webデザインなど幅広い場面で使われる便利なファイル形式です。
							ただしEC出品では「白背景変換」が必要な場合が多く、透過PNGをそのまま使うと問題が起きることもあります。
						</p>
						<p className="text-gray-700 mb-4">
							しろぬきやを使えば、透過PNGの作成から白背景JPEGへの変換まで、ブラウザだけでまとめて対応できます。
							登録不要・無料でお試しください。
						</p>

						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
